import React, { ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import PinLegendType from "@mapmycustomers/shared/enum/PinLegendType";
import PinLegendVisibility from "@mapmycustomers/shared/enum/PinLegendVisibility";
import RoleEnum from "@mapmycustomers/shared/enum/Role";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import Role from "@mapmycustomers/shared/types/Role";
import Team from "@mapmycustomers/shared/types/Team";
import User, { UserRef } from "@mapmycustomers/shared/types/User";

import InlineVisibilitySelectField from "@app/component/input/inline/InlineVisibilitySelectField";
import UserSelectField from "@app/component/input/UserSelectField";
import { getRole } from "@app/store/iam";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import usePinLegendVisibilityOptions from "@app/util/hook/usePinLegendVisibilityOptions";
import { getPinLegendVisibilityNote } from "@app/util/ui";

import styles from "./DetailsForm.module.scss";
import EntityTypeSelectField from "./EntityTypeSelectField";
import FieldNameSelectField from "./FieldNameSelectField";

interface Props {
  creating: boolean;
  creator?: UserRef;
  entityType: EntityTypesSupportedByMapsPage;
  fieldName?: string;
  onCreatorChange?: (user: undefined | UserRef) => void;
  onEntityTypeChange?: (entityType: EntityTypesSupportedByMapsPage) => void;
  onFieldNameChange?: (fieldName?: string) => void;
  onTeamIdsChange?: (teamIds: Team["id"][]) => void;
  onVisibilityChange?: (visibility: PinLegendVisibility) => void;
  role: Role;
  teamIds?: Team["id"][];
  teams: Team[];
  type: PinLegendType;
  users: User[];
  visibility: PinLegendVisibility;
}

const DetailsForm: React.FC<Props> = ({
  creating,
  creator,
  entityType,
  fieldName,
  onCreatorChange,
  onEntityTypeChange,
  onFieldNameChange,
  onTeamIdsChange,
  onVisibilityChange,
  role,
  teamIds,
  teams,
  type,
  users,
  visibility,
}) => {
  const intl = useIntl();
  const visibilityOptions = usePinLegendVisibilityOptions(role);
  const isOwner = role.key === RoleEnum.OWNER;

  const analytics = useAnalytics();

  const handleEntityTypeChange = useCallback(
    (entityType: EntityTypesSupportedByMapsPage) => {
      onEntityTypeChange?.(entityType);
      analytics.clicked(["Entity"], { entityType });
    },
    [analytics, onEntityTypeChange]
  );

  const handleFieldNameChange = useCallback(
    (fieldName?: string) => {
      onFieldNameChange?.(fieldName);
      if (fieldName) {
        analytics.clicked(["Field"], { fieldName });
      }
    },
    [analytics, onFieldNameChange]
  );

  return (
    <div className={styles.container}>
      {isOwner && !creating && (
        <UserSelectField
          defaultUsersOption={false}
          inline
          label={intl.formatMessage({
            id: "map.colorization.createEditSidebar.edit.creator",
            defaultMessage: "Creator",
            description: "Title of the Creator field in Edit pin legend drawer",
          })}
          onChange={onCreatorChange}
          users={users}
          value={creator}
        />
      )}
      <InlineVisibilitySelectField<PinLegendVisibility>
        label={intl.formatMessage({
          id: "map.colorization.createEditSidebar.detailsForm.visibility.label",
          defaultMessage: "Share with",
          description: "Visibility field label in Create Pin Legend drawer on the Map page",
        })}
        noteGetter={getPinLegendVisibilityNote}
        onChange={onVisibilityChange}
        onTeamIdsChange={onTeamIdsChange}
        options={visibilityOptions}
        teamIds={teamIds}
        teams={teams}
        value={visibility}
      />
      <EntityTypeSelectField
        disabled={!creating}
        label={intl.formatMessage(
          {
            id: "map.colorization.createEditSidebar.detailsForm.entityType.label",
            defaultMessage: "Record type",
            description: "EntityType field label in Create Pin Legend drawer on the Map page",
          },
          { b: (text: ReactNode[]) => <b>{text}</b> }
        )}
        onChange={handleEntityTypeChange}
        value={entityType}
      />
      <FieldNameSelectField
        creating={creating}
        entityType={entityType}
        label={intl.formatMessage({
          id: "map.colorization.createEditSidebar.detailsForm.fieldName.label",
          defaultMessage: "Field",
          description: "Field field label in Create Pin Legend drawer on the Map page",
        })}
        onChange={handleFieldNameChange}
        type={type}
        value={fieldName}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  role: getRole(state)!, // pretty sure user details are loaded at this point
  teams: getTeams(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(DetailsForm);
